import {
  type AccordionItemContentProps,
  type AccordionItemProps,
  type AccordionItemTriggerProps,
  type AccordionRootProps,
  Accordion as BaseAccordion,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";

type VariantsProps = "primary" | "secondary" | "help";
type Size = "sm" | "md" | "lg";
type AccordionContentProps = {
  variant: VariantsProps;
  size: Size;
};

// Context to pass props to the child components
const AccordionInternalContext = React.createContext<AccordionContentProps>({
  variant: "primary",
  size: "md",
});

const useAccordionVariantContext = () => {
  const Context = React.useContext(AccordionInternalContext);

  return Context;
};

interface AccordionProps extends AccordionRootProps {
  variant?: VariantsProps;
  size?: Size;
}

interface AccordionTriggerProps extends AccordionItemTriggerProps {
  classNames?: {
    trigger?: string;
    indicator?: string;
    icon?: string;
  };
}

const Accordion = React.forwardRef<
  React.ElementRef<typeof BaseAccordion.Root>,
  AccordionProps
>((props, ref) => {
  const { className, variant = "primary", size = "md", ...rest } = props;
  return (
    <AccordionInternalContext.Provider
      value={{
        variant,
        size,
      }}
    >
      <BaseAccordion.Root
        data-variant={variant}
        data-size={size}
        className={clsx("ox-reset ox-AccordionRoot", className)}
        ref={ref}
        {...rest}
      />
    </AccordionInternalContext.Provider>
  );
});

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof BaseAccordion.ItemTrigger>,
  AccordionTriggerProps
>((props, ref) => {
  const { variant, size } = useAccordionVariantContext();
  const { className, classNames, children, ...rest } = props;

  return (
    <BaseAccordion.ItemTrigger
      className={clsx(
        "ox-reset ox-AccordionTrigger",
        className,
        classNames?.trigger,
      )}
      data-variant={variant}
      data-size={size}
      ref={ref}
      {...rest}
    >
      {variant === "secondary" || variant === "help" ? (
        <span className={clsx("ox-AccordionTriggerIcon", classNames?.icon)}>
          {variant === "secondary" ? (
            <React.Fragment>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                data-part="icon-closed"
                height="18"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
              </svg>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                data-part="icon-expanded"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128Z"></path>
              </svg>
            </React.Fragment>
          ) : variant === "help" ? (
            <React.Fragment>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
              </svg>
            </React.Fragment>
          ) : null}
        </span>
      ) : null}

      <span
        className={clsx(
          "ox-AccordionTriggerText",
          size === "md"
            ? "ox-fz-md"
            : size === "sm"
              ? "ox-fz-sm"
              : size === "lg"
                ? "ox-fz-lg"
                : "",
        )}
      >
        {children}
      </span>
      {variant === "primary" ? (
        <BaseAccordion.ItemIndicator
          className={clsx("ox-AccordionItemIndicator", classNames?.indicator)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="currentColor"
            viewBox="0 0 256 256"
          >
            <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path>
          </svg>
        </BaseAccordion.ItemIndicator>
      ) : null}
    </BaseAccordion.ItemTrigger>
  );
});

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof BaseAccordion.Item>,
  AccordionItemProps
>((props, ref) => {
  const { variant, size } = useAccordionVariantContext();
  const { className, ...rest } = props;
  return (
    <BaseAccordion.Item
      data-variant={variant}
      data-size={size}
      className={clsx("ox-reset ox-AccordionItem", className)}
      ref={ref}
      {...rest}
    />
  );
});

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof BaseAccordion.ItemContent>,
  AccordionItemContentProps
>((props, ref) => {
  const { variant, size } = useAccordionVariantContext();
  const { className, children, ...rest } = props;
  return (
    <BaseAccordion.ItemContent
      data-variant={variant}
      data-size={size}
      className={clsx(
        "ox-reset ox-AccordionContent",
        size === "md"
          ? "ox-fz-md"
          : size === "sm"
            ? "ox-fz-sm"
            : size === "lg"
              ? "ox-fz-lg"
              : "",
        className,
      )}
      ref={ref}
      {...rest}
    >
      <div className={clsx("ox-reset ox-AccordionContentInner")}>
        {children}
      </div>
    </BaseAccordion.ItemContent>
  );
});

const AccordionContext = BaseAccordion.Context;

Accordion.displayName = "Accordion";
AccordionItem.displayName = "AccordionItem";
AccordionTrigger.displayName = "AccordionTrigger";
AccordionContent.displayName = "AccordionContent";
export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  AccordionContext,
};
export type { AccordionProps };
